import React from "react";
import { legalCompanyDetails } from "./constants";
import type { LegalDocumentDefinition } from "./types";

const { brandName, serviceName } = legalCompanyDetails;

const storageInventory = [
  {
    name: "affinity (cookie)",
    provider: `${brandName} hosting platform`,
    purpose: "Keeps requests routed to the same application instance.",
    requirement: "Necessary",
    duration: "2 days",
    party: "First party",
  },
  {
    name: "auth-session (cookie)",
    provider: `${brandName} authentication service`,
    purpose: "Stores a signed, opaque session identifier for web sign-in. Authentication tokens remain server-side.",
    requirement: "Necessary",
    duration: "30 days from the latest session save",
    party: "First party",
  },
  {
    name: "cookie-consent (cookie)",
    provider: brandName,
    purpose: "Records whether optional analytics were accepted or rejected on the website.",
    requirement: "Necessary",
    duration: "180 days from the latest choice",
    party: "First party",
  },
  {
    name: "service-token (session storage)",
    provider: brandName,
    purpose: "Temporarily caches the access token used by the authenticated web application.",
    requirement: "Necessary",
    duration: "Until the browser tab or window session ends; also removed on sign-out or when invalid",
    party: "First party",
  },
  {
    name: "auth-logout-event (local storage)",
    provider: brandName,
    purpose: "Notifies other open tabs that sign-out occurred. The value is a timestamp and random event marker.",
    requirement: "Necessary",
    duration: "Until overwritten or browser storage is cleared",
    party: "First party",
  },
  {
    name: "selected-household (local storage)",
    provider: brandName,
    purpose: "Remembers which household the user selected.",
    requirement: "Necessary",
    duration: "Until changed or browser storage is cleared",
    party: "First party",
  },
  {
    name: "meal-plan-shopping-list-preview (local storage)",
    provider: brandName,
    purpose: "Preserves temporary shopping-list selections and quantity changes while a list is being prepared.",
    requirement: "Necessary",
    duration: "Until the list is created, another meal plan is opened, or browser storage is cleared",
    party: "First party",
  },
  {
    name: "scroll-restoration:<container>:<history-entry> (session storage)",
    provider: brandName,
    purpose: "Restores list position and loaded-item count when navigating within the application.",
    requirement: "Necessary",
    duration: "Until the browser tab or window session ends",
    party: "First party",
  },
  {
    name: "cookie-consent (Capacitor Preferences)",
    provider: `${brandName}, using Capacitor Preferences`,
    purpose: "Records whether optional analytics were accepted or rejected in the native app.",
    requirement: "Necessary",
    duration: "Until the choice is changed, app data is cleared, or the app is removed",
    party: "First-party app storage",
  },
  {
    name: "com.realvore.authentik / auth_state (Android private app preferences)",
    provider: `${brandName} authentication service`,
    purpose: "Maintains the native Android OpenID Connect sign-in state and tokens.",
    requirement: "Necessary",
    duration: "Until sign-out, app data is cleared, or the app is removed",
    party: "First-party app storage",
  },
  {
    name: "com.realvore.authentik / com.realvore.ios-auth-state (iOS Keychain)",
    provider: `${brandName} authentication service`,
    purpose: "Maintains the native iOS OpenID Connect sign-in state and tokens in the device Keychain.",
    requirement: "Necessary",
    duration: "Until sign-out or the device's Keychain data is erased",
    party: "First-party app storage",
  },
  {
    name: "_hp2_id.APP_ID (cookie)",
    provider: "Heap",
    purpose: "Stores Heap's user identifier, identity, and related identifiers after analytics consent.",
    requirement: "Optional",
    duration: "13 months minus 1 day",
    party: "First-party cookie managed by Heap",
  },
  {
    name: "_hp2_ses_props.APP_ID (cookie)",
    provider: "Heap",
    purpose: "Stores Heap session properties, including a timestamp and cookie domain/path.",
    requirement: "Optional",
    duration: "30 minutes",
    party: "First-party cookie managed by Heap",
  },
  {
    name: "_hp2_props.APP_ID (cookie)",
    provider: "Heap",
    purpose: "Stores event properties used by Heap analytics.",
    requirement: "Optional",
    duration: "13 months minus 1 day",
    party: "First-party cookie managed by Heap",
  },
  {
    name: "_hp2_hld. (cookie)",
    provider: "Heap",
    purpose: "Temporarily checks the highest domain on which Heap can set its first-party cookies.",
    requirement: "Optional",
    duration: "Temporary; should not persist",
    party: "First-party cookie managed by Heap",
  },
  {
    name: "ld:<client-side-environment-id>:$diagnostics (local storage)",
    provider: "LaunchDarkly",
    purpose: "Stores SDK diagnostic counters and the last diagnostic reporting time for feature-flag reliability.",
    requirement: "Necessary",
    duration: "Until overwritten by the SDK or browser storage is cleared",
    party: "First-party storage managed by LaunchDarkly",
  },
  {
    name: "No persistent browser or device-storage key (current Sentry configuration)",
    provider: "Sentry",
    purpose: "Operational error monitoring. Session Replay, offline event storage, and browser tracing storage are not enabled.",
    requirement: "Necessary",
    duration: "Not applicable",
    party: "No client-side storage",
  },
] as const;

export const cookieDocument: LegalDocumentDefinition = {
  id: "cookies",
  title: "Cookie Policy",
  slug: "/cookies",
  effectiveDate: "2026-08-04",
  render: (renderLink) => (
    <>
      <p>
        This Cookie Policy explains how {brandName} uses cookies and similar technologies on our public site and
        authenticated application. It should be read together with our{" "}
        {renderLink({ href: "/privacy", children: "Privacy Policy" })}.
      </p>

      <h5>1. What cookies and similar technologies are</h5>
      <p>
        Cookies, local storage, SDKs, and similar technologies store or read information on your browser or device. We
        use them to keep {serviceName} working, remember choices, improve reliability, and, where accepted, understand
        product usage.
      </p>

      <h5>2. Cookie and storage inventory</h5>
      <p>
        This inventory covers the technologies currently used by our public website, authenticated web application,
        and native apps. “Necessary” means the technology supports authentication, security, preferences, feature
        delivery, reliability, or core app state and is not controlled by the optional analytics choice. “First party”
        describes where the cookie or storage is placed; a named service provider may still manage it for us. In the
        Heap names below, <code>APP_ID</code> is replaced by the Heap environment identifier.
      </p>
      <table className="storage-inventory">
          <thead>
            <tr>
              <th scope="col">Name/key</th>
              <th scope="col">Provider</th>
              <th scope="col">Purpose</th>
              <th scope="col">Necessary or optional</th>
              <th scope="col">Storage duration</th>
              <th scope="col">First or third party</th>
            </tr>
          </thead>
          <tbody>
            {storageInventory.map((item) => (
              <tr key={item.name}>
                <td data-label="Name/key"><code>{item.name}</code></td>
                <td data-label="Provider">{item.provider}</td>
                <td data-label="Purpose">{item.purpose}</td>
                <td data-label="Necessary or optional">{item.requirement}</td>
                <td data-label="Storage duration">{item.duration}</td>
                <td data-label="First or third party">{item.party}</td>
              </tr>
            ))}
          </tbody>
      </table>

      <h5>3. Optional analytics cookies</h5>
      <p>
        We use Heap for optional product analytics to understand feature usage and improve the service. The Heap SDK is
        first initialized only if you select Accept in the cookie banner or cookie preferences dialog. Depending on the
        features used during a visit, not every Heap cookie listed above will necessarily be present.
      </p>
      <p>
        If you reject analytics before Heap is initialized, Heap cookies are not created by our app. If you withdraw
        consent after previously accepting, we disable future analytics tracking and reset the Heap identity, but
        existing Heap cookies may remain until they expire or you clear them in your browser.
      </p>

      <h5>4. Necessary feature delivery and error monitoring</h5>
      <p>
        LaunchDarkly supplies feature flags used to deliver and safely roll out application functionality. The contexts
        we provide already contain an explicit key, so the SDK does not need to create its optional anonymous-user ID
        storage key. It does store the diagnostics entry listed above.
      </p>
      <p>
        We use Sentry for operational error monitoring and reliability. Sentry is used to identify crashes, performance
        issues, and technical failures. It is not used for behavioural analytics. With our current configuration,
        Sentry does not set a cookie or persistent browser/device-storage key, and Session Replay is not enabled.
      </p>
      <p>
        We configure Sentry to avoid collecting recipe content, allergy data, passwords, payment details, or form field
        contents unnecessarily.
      </p>

      <h5>5. Changing your choice</h5>
      <p>
        You can reopen Cookie preferences at any time from the footer on the website or in the app. If you withdraw
        consent, future Heap analytics tracking stays disabled. You can also remove stored browser data using your
        browser settings. Removing necessary storage may sign you out, forget app choices, or prevent parts of the
        service from working as expected.
      </p>

      <h5>6. More information</h5>
      <p>
        For more detail about how we handle personal data, read our{" "}
        {renderLink({ href: "/privacy", children: "Privacy Policy" })}. For the rules that govern the service, read our{" "}
        {renderLink({ href: "/tos", children: "Terms of Service" })}. Food, nutrition, allergy, and AI-assisted
        information is covered by our {renderLink({ href: "/disclaimer", children: "Disclaimer" })}.
      </p>
    </>
  ),
};
